import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { OrdersToolbarFilters } from '@/hooks/useOrdersToolbarFilters';
import FilterSurface from './FilterSurface';
import { MenuField } from './FilterMenu';
import { FOCUS_RING, formatCount } from './toolbarConfig';

/**
 * Applied filters as editable tokens — "Status is On Hold ×". The token body
 * reopens its picker; × removes it. Then Clear and the result count.
 * Rendered only when at least one filter is applied.
 */
interface Props {
  /** Sits inside the header row (lg…xl) — no own border/padding. */
  inline?: boolean;
  filters: OrdersToolbarFilters;
  total?: number;
  openField: MenuField | null;
  onOpenField: (field: MenuField | null) => void;
}

const FilterTokens = ({ inline, filters, total, openField, onOpenField }: Props) => {
  const { t } = useTranslation();
  return (
    <div
      className={
        inline
          ? 'flex items-center gap-2 min-w-0'
          : 'flex items-center gap-2 min-h-[44px] px-3 py-2 border-0 border-t border-solid border-sd-border'
      }
    >
      <ul
        aria-label={String(t('applied_filters'))}
        className={`flex items-center gap-1.5 m-0 p-0 list-none min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${inline ? '[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] rtl:[mask-image:linear-gradient(to_left,black_calc(100%-24px),transparent)] pe-6' : 'max-sm:[mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] max-sm:rtl:[mask-image:linear-gradient(to_left,black_calc(100%-24px),transparent)] max-sm:pe-6 sm:flex-wrap sm:overflow-visible'}`}
      >
        {filters.chips.map((chip) => {
          const text = [chip.label, chip.op && String(t(chip.op)), chip.value]
            .filter(Boolean)
            .join(' ');
          const field = chip.key === 'partial' ? null : (chip.key as MenuField);
          const body = (
            <span className='inline-flex items-center gap-1 min-w-0 truncate'>
              {chip.label ? <span className='text-sd-muted-foreground'>{chip.label}</span> : null}
              {chip.op ? (
                <span className='text-sd-muted-foreground'>{String(t(chip.op))}</span>
              ) : null}
              <bdi dir={chip.ltr ? 'ltr' : undefined} className='font-semibold truncate'>
                {chip.value}
              </bdi>
            </span>
          );
          const bodyClass = `inline-flex items-center h-full ps-2.5 pe-1 min-w-0 max-w-[260px] bg-transparent border-0 text-[12px] text-sd-foreground rounded-s-lg ${FOCUS_RING}`;
          return (
            <li key={chip.key} className={`shrink-0 min-w-0 ${inline ? '' : 'sm:shrink'}`}>
              <span className='inline-flex items-center h-7 rounded-lg border border-solid border-sd-border bg-sd-card hover:border-gray-300 transition-colors max-w-full'>
                {field ? (
                  <FilterSurface
                    filters={filters}
                    field={field}
                    placement='bottomLeft'
                    open={openField === field}
                    onOpenChange={(o) => onOpenField(o ? field : null)}
                  >
                    <button
                      type='button'
                      title={text}
                      aria-label={`${String(t('edit_filter'))}: ${text}`}
                      aria-haspopup='dialog'
                      aria-expanded={openField === field}
                      className={`${bodyClass} cursor-pointer`}
                    >
                      {body}
                    </button>
                  </FilterSurface>
                ) : (
                  <span className={bodyClass}>{body}</span>
                )}
                <button
                  type='button'
                  onClick={chip.onRemove}
                  aria-label={`${String(t('remove_filter'))}: ${text}`}
                  className={`inline-flex items-center justify-center w-6 h-full rounded-e-lg bg-transparent border-0 cursor-pointer text-sd-muted-foreground hover:text-sd-foreground hover:bg-sd-muted shrink-0 ${FOCUS_RING}`}
                >
                  <X aria-hidden className='w-3.5 h-3.5' />
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      <button
        type='button'
        onClick={filters.clearAll}
        className={`shrink-0 h-7 px-2 rounded-lg bg-transparent border-0 cursor-pointer text-[12px] font-semibold text-sd-muted-foreground hover:text-sd-foreground hover:bg-sd-muted ${FOCUS_RING}`}
      >
        {String(t('clear'))}
      </button>
      {total !== undefined ? (
        <span
          aria-live='polite'
          className='shrink-0 text-[12px] text-sd-muted-foreground tabular-nums whitespace-nowrap'
        >
          {String(t('orders_result_count', { n: formatCount(total) }))}
        </span>
      ) : null}
    </div>
  );
};

export default FilterTokens;
